import { Link } from '@tanstack/react-router';
import { Globe, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useThemeAndLanguage } from '@/hooks/useThemeAndLanguage';
import { toastSuccess } from '@/lib/toast-helpers';
import { useAuthStore } from '@/stores';
import type { Language } from '@/stores/ui-store';
import { Navigation } from './Navigation';

interface AppHeaderProps {
  readonly onLoginClick?: () => void;
}

export function AppHeader({ onLoginClick }: AppHeaderProps) {
  const { t } = useTranslation('common');
  const { user, signOut } = useAuthStore();
  const { online } = useNetworkStatus();
  const { theme, language, toggleTheme, changeLanguage } = useThemeAndLanguage();

  const handleSignOut = async () => {
    try {
      await signOut();
      toastSuccess(t('auth.logout'));
    } catch {
      // Error is handled by the store
    }
  };

  const handleLanguageChange = (value: string) => {
    changeLanguage(value as Language);
  };

  const settingsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('settings.title')}>
          <Settings className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44" align="end" forceMount>
        <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
          {theme === 'light' ? (
            <>
              <Moon className="mr-2 h-4 w-4" />
              <span>{t('theme.label')}</span>
            </>
          ) : (
            <>
              <Sun className="mr-2 h-4 w-4" />
              <span>{t('theme.label')}</span>
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Globe className="mr-2 h-4 w-4" />
            <span>{t('language.label')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={language} onValueChange={handleLanguageChange}>
              <DropdownMenuRadioItem value="en" className="cursor-pointer">
                <span>{t('language.en')}</span>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="pt-BR" className="cursor-pointer">
                <span>{t('language.pt-BR')}</span>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <Link to="/settings">
          <DropdownMenuItem className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            <span>{t('settings.title')}</span>
          </DropdownMenuItem>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full max-w-6xl mx-auto px-4 flex h-14 items-center justify-between">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="md:hidden" />
          <Link to="/" className="flex items-center space-x-2">
            <Logo className="h-8 w-8 text-foreground" />
            <span className="font-bold">{t('app.name')}</span>
          </Link>
        </div>
        <div className="hidden md:flex flex-1 items-center justify-center px-4">
          <Navigation />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center" aria-live="polite">
            <span
              className={`inline-block h-2 w-2 rounded-full ${online ? 'bg-emerald-500' : 'bg-amber-500'}`}
              aria-label={online ? 'Online' : 'Offline'}
              title={online ? 'Online' : 'Offline'}
            />
          </div>
          <div className="hidden md:block">{settingsMenu}</div>
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={
                          user.user_metadata &&
                          typeof user.user_metadata === 'object' &&
                          'avatar_url' in user.user_metadata &&
                          typeof user.user_metadata.avatar_url === 'string'
                            ? user.user_metadata.avatar_url
                            : undefined
                        }
                        alt={user.email ?? ''}
                      />
                      <AvatarFallback>{user.email?.charAt(0).toUpperCase() ?? 'U'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="end" forceMount>
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium leading-none truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      void handleSignOut();
                    }}
                    className="cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onLoginClick}>
                {t('auth.login')}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
